import React, { ReactNode, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * `common/Modal` (M-UX-2.3, architecture-ux-restructure.md §4.3).
 *
 * ZENTRAL overlay primitive: portal-rendered, focus-trapped, Escape-to-close,
 * body-scroll-locked while open, and returns focus to the element that had
 * it before the modal opened. This is the single place these behaviours are
 * implemented — no page/dialog should hand-roll them again (`ConfirmationDialog`
 * migration to this component is M-UX-2.4, out of scope here).
 */
export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  size?: 'sm' | 'md' | 'lg';
  children: ReactNode;
  footer?: ReactNode;
}

const SIZE_CLASSES: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
};

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement
  );
}

export function Modal({ open, onClose, title, size = 'md', children, footer }: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const titleId = useId();

  // `onClose` is frequently passed as a fresh inline arrow by callers
  // (e.g. `onClose={() => setDialogOpen(false)}`), so it is NOT a safe
  // dependency for the mount/focus-trap effect below: if that effect
  // depended on it directly, every parent re-render while the modal is
  // open would tear the effect down and set it up again, re-running the
  // initial-focus `.focus()` call AND the cleanup's `previouslyFocusedRef
  // .focus()` call on every render. That creates a focus tug-of-war with
  // whatever blur/focus handlers exist on the surrounding form (e.g.
  // field validation), which triggers more state updates, which trigger
  // more re-renders — a real "Maximum update depth exceeded" loop
  // observed when submitting via `form.requestSubmit()` / Enter (bug
  // found in the M-UX-2.4 E2E gate). Keep the latest `onClose` in a ref
  // instead, so the effect only depends on `open`.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;

    // Scroll-Lock
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Initial focus: first focusable element inside the panel, else the panel itself.
    const panel = panelRef.current;
    if (panel) {
      const focusable = getFocusable(panel);
      (focusable[0] ?? panel).focus();
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (event.key === 'Tab' && panelRef.current) {
        const focusable = getFocusable(panelRef.current);
        if (focusable.length === 0) {
          event.preventDefault();
          return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalOverflow;
      previouslyFocusedRef.current?.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onClose intentionally excluded, see onCloseRef above
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/60" aria-hidden="true" onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`relative flex max-h-[90vh] w-full flex-col rounded-card border border-default bg-surface-raised text-primary shadow-[var(--shadow-overlay)] focus:outline-none ${SIZE_CLASSES[size]}`}
      >
        <div className="flex items-center justify-between border-b border-default px-6 py-4">
          <h2 id={titleId} className="text-lg font-semibold">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="rounded-control p-1 text-muted hover:bg-surface-inset hover:text-primary"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <div className="overflow-y-auto px-6 py-4">{children}</div>
        {footer && <div className="flex items-center justify-end gap-2 border-t border-default px-6 py-4">{footer}</div>}
      </div>
    </div>,
    document.body
  );
}

export default Modal;
